import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'
import { Breadcrumbs, IconButton, InputAdornment, TextField, Typography } from '@mui/material'

import { FormError } from '../types'
import { ReportStatus } from '../lib'
import Chip from '../components/common/chip'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import TrashIcon from '../assets/icons/trash-icon.svg'
import SearchIcon from '../assets/icons/search-icon.svg'
import Pagination from '../components/common/pagination'
import { textFieldWhiteBg } from '../theme/custom-styles'
import { handleIsValidMatch } from '../utils/common.utils'
import DownloadIcon from '../assets/icons/download-icon.svg'
import CalendarIcon from '../assets/icons/calendar-icon.svg'
import {getDownloadAttachmentFilePreSignedURL, resolveDocumentsAccessToken } from '../api/documents.api'
import { getRequestedReportsByCompanyId, updateRequestedReport } from '../api/payments.api'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'

const RequestedReportsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry')?.toLowerCase() || 'col'

  const navigate = useNavigate()
  const { closeSnackbar } = useSnackbar()
  const { customerCompanyId } = useParams()
  const currentListPageRef = useRef<number>(1)
  const { userSession } = useContext(UserContext)
  const componentDidMount = useRef<boolean>(false)
  const reportsHistoryDataRef = useRef<Array<any>>([])
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['reports-management', 'common'])
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { setScrollToTop, updateLoadingStatus } = useContext(AppStateContext)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [reportToSearch, setReportToSearch] = useState<string>('')
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [reportsHistoryData, setReportsHistoryData] = useState<Array<any>>([])
  const [reportsHistoryList, setReportsHistoryList] = useState<Array<any>>([])
  const [openDeleteRequestedReportModal, setOpenDeleteRequestedReportModal] = useState(false)
  const [searchedReportsHistoryData, setSearchedReportsHistoryData] = useState<Array<any>>([])
  const [dialogDeleteRequestedReportIsLoading, setDialogDeleteRequestedReportIsLoading] = useState(false)
  const [selectedRequestedReportData, setSelectedRequestedReportData] = useState<{ reportId: string; reportName: string } | null>(null)

  useEffect(() => {
    closeSnackbar()

    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'admin' && userSession?.role !== 'super-admin' && userSession?.role !== 'admin-client' && userSession?.role !== 'super-admin-client') {
      navigate('/404')
      return
    }

    handleGetRequestedReportsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on('paymentsReportCreated', async ({ reportId, country, userSessionId }: { reportId: string; country: string; userSessionId: string }) => {
      if (userSession && String(userSession._id) === String(userSessionId) && country === selectedCountry) {
        handleUpdateReportStatus(reportId, ReportStatus.COMPLETED)
      }
    })

    appDataSocket.on('paymentsReportCreationError', async ({ reportId, country, status, userSessionId }: { reportId: string; country: string; status: ReportStatus; userSessionId: string }) => {
      if (userSession && String(userSession._id) === String(userSessionId) && country === selectedCountry) {
        handleUpdateReportStatus(reportId, status)
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, userSession, isAppDataSocketInitialized])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const reportsPerPage = reportToSearch === '' ? reportsHistoryData.slice(firstPageIndex, lastPageIndex) : searchedReportsHistoryData.slice(firstPageIndex, lastPageIndex)

    setReportsHistoryList(reportsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, reportsHistoryData, searchedReportsHistoryData])

  const handleUpdateReportStatus = async (reportId: string, statusToSet: ReportStatus) => {
    const updatedReportsHistoryData = [...reportsHistoryDataRef.current]
    const reportIndex = updatedReportsHistoryData.findIndex((reportData) => String(reportData._id) === String(reportId)) ?? -1

    if (reportIndex > -1) {
      updatedReportsHistoryData[reportIndex].status = statusToSet
      reportsHistoryDataRef.current = [...updatedReportsHistoryData]

      setReportsHistoryData([...updatedReportsHistoryData])
    }
  }

  const handleGetRequestedReportsData = async () => {
    try {
      const {
        data: { requestedReports }
      } = await getRequestedReportsByCompanyId(String(customerCompanyId), selectedCountry)

      setReportsHistoryData(requestedReports)
      reportsHistoryDataRef.current = requestedReports

      if (componentDidMount.current === false) {
        componentDidMount.current = true
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }

      return
    }
  }

  const handleSetReportToSearch = (reportToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1

    setErrors({})
    setCurrentListPage(1)
    setReportToSearch(reportToSearchData)

    if (reportToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentReportsHistoryDataState = reportsHistoryDataRef.current ?? []
      const reportsHistoryPerPage = currentReportsHistoryDataState.slice(firstPageIndex, lastPageIndex)

      setReportsHistoryList(reportsHistoryPerPage)
      setSearchedReportsHistoryData([])

      if (showError) setErrors({ reportToSearch: t('generatedReports.searchField.errors.emptyField', { ns: 'reports-management' }) })
      return
    }

    const currentFullReportsHistoryData = [...(reportsHistoryDataRef.current as Array<any>)]
    const filteredReportsHistory = currentFullReportsHistoryData.filter((reportData) => {
      const documentName = handleIsValidMatch(reportToSearchData.toLowerCase(), String(reportData.documentName).toLowerCase())
      const requestedAt = handleIsValidMatch(reportToSearchData.toLowerCase(), String(format(new Date(reportData.requestedAt), 'dd/MM/yyyy | HH:mm:ss')).toLowerCase())
      const generatedAt = handleIsValidMatch(reportToSearchData.toLowerCase(), String(format(new Date(reportData.generatedAt || 0), 'dd/MM/yyyy | HH:mm:ss')).toLowerCase())

      const reportStatusText =
        reportData.status === ReportStatus.COMPLETED
          ? t('generatedReports.reportStatusText.generated', { ns: 'reports-management' })
          : reportData.status === ReportStatus.ERROR
            ? t('generatedReports.reportStatusText.error', { ns: 'reports-management' })
            : t('generatedReports.reportStatusText.processing', { ns: 'reports-management' })
      const reportStatus = handleIsValidMatch(reportToSearchData.toLowerCase(), String(reportStatusText).toLowerCase())

      return documentName || requestedAt || generatedAt || reportStatus
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const reportsHistoryPerPage = filteredReportsHistory.slice(firstPageIndex, lastPageIndex)

    setReportsHistoryList(reportsHistoryPerPage)
    setSearchedReportsHistoryData(filteredReportsHistory)
  }

  const handleDownloadReport = async (reportName: string) => {
    updateLoadingStatus?.(true)

    try {
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getDownloadAttachmentFilePreSignedURL({ attachmentFilename: reportName }, accessToken)

      window.open(data, '_blank')
      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleConfirmRequestedReportDeletion = async () => {
    try {
      setDialogDeleteRequestedReportIsLoading(true)
      await updateRequestedReport(String(selectedRequestedReportData?.reportId), { deleted: true })

      const currentReportsHistoryData = [...reportsHistoryDataRef.current]
      const updatedReportsHistoryData = currentReportsHistoryData.filter((reportData) => String(reportData._id) !== String(selectedRequestedReportData?.reportId))

      reportsHistoryDataRef.current = [...updatedReportsHistoryData]
      setReportsHistoryData([...updatedReportsHistoryData])
      setOpenDeleteRequestedReportModal(false)
      updateNewNotificationStatus?.(true, 'requested-report-deleted-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setDialogDeleteRequestedReportIsLoading(false)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div className="section-container">
        <Breadcrumbs aria-label="breadcrumb">
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('generatedReports.title')}
          </Typography>
        </Breadcrumbs>
        <div className="customer-company-fundings-history-container">
          <div className="actions-bar">
            <div className="left-content">
              <div className="title">
                {reportsHistoryDataRef.current.length === 0
                  ? t('generatedReports.actionsBar.thereAreNoReportsHistory')
                  : reportToSearch === ''
                    ? t('generatedReports.actionsBar.generatedReportsCount', { reportsCount: reportsHistoryDataRef.current.length })
                    : t('generatedReports.actionsBar.generatedReportsCount', { reportsCount: searchedReportsHistoryData.length })}
              </div>
            </div>
            <TextField
              variant="standard"
              sx={textFieldWhiteBg}
              value={reportToSearch}
              className="search-input"
              error={!!errors.reportToSearch}
              helperText={errors.reportToSearch}
              label={t('generatedReports.searchField.placeholder')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleSetReportToSearch(reportToSearch, true)
                }
              }}
              onChange={(event) => handleSetReportToSearch(event.target.value, true)}
              onBlur={() => {
                if (reportToSearch === '') setErrors({})
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                    >
                      <img
                        src={SearchIcon}
                        alt="search-icon"
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
        </div>
        {(componentDidMount.current === false || (componentDidMount.current === true && reportsHistoryDataRef.current.length > 0)) && (
          <div className="table-header-container table-header-batches">
            <div className="text-header table-header-width-20">{t('generatedReports.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}</div>
            <div className="text-header table-header-width-40">{t('generatedReports.tableHeaders.reportName', { ns: 'reports-management' }).toUpperCase()}</div>
            <div className="text-header table-header-width-20 num-of-payments-batches customer-company-display-none-lg">{t('generatedReports.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}</div>
            <div className="text-header table-header-width-20 status-batches">{t('generatedReports.tableHeaders.actions', { ns: 'reports-management' }).toUpperCase()}</div>
          </div>
        )}
        {componentDidMount.current === false && (
          <div className="users-management-users-list-container">
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
          </div>
        )}
        {componentDidMount.current === true && reportsHistoryDataRef.current.length > 0 && (
          <>
            <div className="documents-list-container">
              {reportsHistoryList.map((reportHistoryData: any, index: number) => {
                const reportStatus = reportHistoryData.status === ReportStatus.COMPLETED ? 'success' : reportHistoryData.status === ReportStatus.ERROR ? 'error' : 'processing'
                const reportStatusText =
                  reportHistoryData.status === ReportStatus.COMPLETED
                    ? t('generatedReports.reportStatusText.generated', { ns: 'reports-management' })
                    : reportHistoryData.status === ReportStatus.ERROR
                      ? t('generatedReports.reportStatusText.error', { ns: 'reports-management' })
                      : t('generatedReports.reportStatusText.processing', { ns: 'reports-management' })

                return (
                  <div
                    key={index}
                    className="customer-details-list-item-data-container"
                    onClick={() => {
                      if (reportHistoryData.status === ReportStatus.COMPLETED) {
                        handleDownloadReport(reportHistoryData.documentName)
                      }
                    }}
                    style={reportHistoryData.status === ReportStatus.COMPLETED ? { cursor: 'pointer' } : { cursor: 'unset' }}
                  >
                    <div className="customer-company-table-column customer-company-table-width-20 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={CalendarIcon}
                          alt="calendar-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{format(new Date(reportHistoryData.requestedAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-40 max-width-unset">
                      <div className="customer-details-list-item-text">{reportHistoryData.documentName.replace(/\.[^.]*$/, '').toUpperCase()}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-20 customer-company-display-none-lg max-width-unset">
                      <Chip
                        status={reportStatus}
                        label={reportStatusText}
                      />
                    </div>
                    <div
                      style={{ gap: '20px' }}
                      className="customer-company-table-column customer-company-table-width-20 customer-company-display-none-sm max-width-unset"
                    >
                      {reportHistoryData.status === ReportStatus.COMPLETED && (
                        <IconButton
                          edge="end"
                          sx={{ color: '#107569' }}
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDownloadReport(reportHistoryData.documentName)
                          }}
                          aria-label="toggle password visibility"
                        >
                          <img
                            src={DownloadIcon}
                            alt="download-icon"
                          />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        sx={{ color: '#107569' }}
                        onClick={(event) => {
                          event.stopPropagation()
                          setSelectedRequestedReportData({
                            reportId: String(reportHistoryData._id),
                            reportName: String(reportHistoryData.documentName.replace(/\.[^.]*$/, '').toUpperCase())
                          })
                          setOpenDeleteRequestedReportModal(true)
                        }}
                        aria-label="toggle password visibility"
                      >
                        <img
                          src={TrashIcon}
                          alt="trash-icon"
                        />
                      </IconButton>
                    </div>
                  </div>
                )
              })}
            </div>
            <Pagination
              pageSize={pageSize}
              currentPage={currentListPage}
              handlePaginationPageChange={(page: number) => {
                setTimeout(() => {
                  setScrollToTop(true)
                  setCurrentListPage(page)
                  currentListPageRef.current = page
                }, 100)
              }}
              handleChangePageSize={(pageSizeToSet: string) => {
                setScrollToTop(true)
                setPageSize(Number(pageSizeToSet))
              }}
              totalCount={reportToSearch === '' ? reportsHistoryData.length : searchedReportsHistoryData.length}
            />
          </>
        )}
      </div>
      {openDeleteRequestedReportModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          dialogIsLoading={dialogDeleteRequestedReportIsLoading}
          handleDialogAction={handleConfirmRequestedReportDeletion}
          dialogTitle={t('generatedReports.deleteRequestedReportModal.title')}
          actionButtonText={t('generatedReports.deleteRequestedReportModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            setOpenDeleteRequestedReportModal(openState)

            if (openState === false) {
              setErrors({})
              setSelectedRequestedReportData(null)
            }
          }}
        >
          <div className="dialog-content-text">{t('generatedReports.deleteRequestedReportModal.instructionText', { reportName: selectedRequestedReportData?.reportName.toUpperCase() })}</div>
        </Dialog>
      )}
    </>
  )
}

export default RequestedReportsManagement
